import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import utnFrcLogo from '../assets/logos/utnFrcLogo.png';
import logoAve from '../assets/logos/logoave.png';
import ChatModal from './common/ChatModal';
import PublicCaptchaGate from './common/PublicCaptchaGate';
import materiaService from '../services/MateriaService';
import regionalApiService from '../services/RegionalApiService';
import AsistenteService from '../services/AsistenteService';
import publicAccessService from '../services/PublicAccessService';
import {
  isRagFlowAssistant,
  isAssistantProcessingReady,
  getAssistantProcessingPercent,
  getAssistantProcessingLabel,
  getAssistantProcessingDetail
} from '../utils/aveProcessing';
import './SubjectCardsGrid.css';

const getUnavailableAveMessage = (assistant) => {
  const label = getAssistantProcessingLabel(assistant).toLowerCase();
  const percent = getAssistantProcessingPercent(assistant);
  const detail = getAssistantProcessingDetail(assistant);
  const suffix = percent !== null ? ` (${percent}%).` : '.';
  return detail || `El asistente AVE todavia esta ${label}${suffix}`;
};

const SubjectCardsGrid = () => {
  const currentYear = new Date().getFullYear();
  const [isPublicAccessVerified, setIsPublicAccessVerified] = useState(publicAccessService.hasToken());
  const [showModal, setShowModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [publicYears, setPublicYears] = useState([]);
  const [loadingMaterias, setLoadingMaterias] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatAssistant, setChatAssistant] = useState(null);
  const [chatPassword, setChatPassword] = useState('');
  const [chatTitle, setChatTitle] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isValidatingPassword, setIsValidatingPassword] = useState(false);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const location = useLocation();
  const [regionalId, setRegionalId] = useState('');
  const [regionalError, setRegionalError] = useState('');
  const [assistantNickname, setAssistantNickname] = useState('');
  const [directAssistant, setDirectAssistant] = useState(null);
  const [directLoading, setDirectLoading] = useState(false);
  const [directError, setDirectError] = useState('');
  const [directRegionalId, setDirectRegionalId] = useState('');
  const [regionalNombreUrl, setRegionalNombreUrl] = useState('');
  const [directPassword, setDirectPassword] = useState('');
  const [directPasswordError, setDirectPasswordError] = useState('');
  const [directValidating, setDirectValidating] = useState(false);
  const [isResolvingRegional, setIsResolvingRegional] = useState(false);
  const [areaId, setAreaId] = useState('');
  const [areaError, setAreaError] = useState('');
  const [areaName, setAreaName] = useState('');
  const [regionalName, setRegionalName] = useState('');

  const buildSlug = (value) => {
    if (!value) return '';
    return value.trim().toLowerCase().replace(/\s+/g, '_');
  };

  const isCaptchaError = (error) => (error?.message || '').toLowerCase().includes('captcha');

  const resetPublicAccess = () => {
    publicAccessService.clearToken();
    setIsPublicAccessVerified(false);
    setShowModal(false);
    setShowChatModal(false);
  };

  useEffect(() => {
    const handlePublicAccessExpired = () => {
      resetPublicAccess();
    };

    window.addEventListener('public-access-expired', handlePublicAccessExpired);
    return () => window.removeEventListener('public-access-expired', handlePublicAccessExpired);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get('regionalId') || params.get('idRegional') || '';
    const regionalParam = params.get('regional') || '';
    const areaParam = params.get('areaId') || params.get('idArea') || '';
    const assistantParam = params.get('asistente') || params.get('assistant') || params.get('nickname') || '';

    setRegionalError('');
    setAreaError('');
    setDirectError('');
    setDirectAssistant(null);
    setDirectRegionalId('');
    setDirectPassword('');
    setDirectPasswordError('');
    setAreaId(areaParam);
    setAssistantNickname(assistantParam);
    setRegionalNombreUrl(regionalParam);

    if (!areaParam) {
      setIsResolvingRegional(false);
      setRegionalId('');
      setAreaError('Falta el área en la URL.');
      return;
    }

    if (idParam) {
      setIsResolvingRegional(false);
      setRegionalId(idParam);
      return;
    }

    if (!regionalParam) {
      setIsResolvingRegional(false);
      setRegionalId('');
      return;
    }

    const nombreUrl = buildSlug(regionalParam);
    if (!isPublicAccessVerified) {
      setIsResolvingRegional(false);
      setRegionalId('');
      return;
    }

    const resolveRegional = async () => {
      setIsResolvingRegional(true);
      try {
        const response = await regionalApiService.getRegionalByNombreUrl(nombreUrl);
        const list = response?.regionales || response?.Regionales || response?.data || response || [];
        const regional = Array.isArray(list) ? list[0] : list;
        const resolvedId = regional?.id || regional?.Id;
        if (!resolvedId) {
          throw new Error('No se encontró el regional solicitado.');
        }
        setRegionalId(resolvedId);
      } catch (error) {
        setRegionalId('');
        setRegionalError(error.message || 'No pudimos identificar el regional.');
      } finally {
        setIsResolvingRegional(false);
      }
    };

    resolveRegional();
  }, [location.search, isPublicAccessVerified]);

  useEffect(() => {
    const loadDirectAssistant = async () => {
      if (!isPublicAccessVerified) {
        return;
      }

      if (!assistantNickname) {
        setDirectAssistant(null);
        return;
      }

      if (!regionalNombreUrl) {
        setDirectError('Falta el regional en la URL.');
        return;
      }
      if (!areaId) {
        setDirectError('Falta el área en la URL.');
        return;
      }

      setDirectLoading(true);
      setDirectError('');
      try {
        const response = await AsistenteService.getAsistentePublicoPorNickname(assistantNickname, regionalNombreUrl, areaId);
        const asistente = response?.asistente || response?.Asistente || response;
        if (!asistente) {
          throw new Error('No se encontró el asistente solicitado.');
        }

        const resolvedRegionalId = asistente.idRegional || asistente.IdRegional || regionalId;
        if (resolvedRegionalId) {
          setDirectRegionalId(resolvedRegionalId);
          if (resolvedRegionalId !== regionalId) {
            setRegionalId(resolvedRegionalId);
          }
        }

        if (isRagFlowAssistant(asistente) && !isAssistantProcessingReady(asistente)) {
          setDirectAssistant(asistente);
          setDirectError(getUnavailableAveMessage(asistente));
          return;
        }

        setDirectAssistant(asistente);
        const requierePassword = asistente.requierePassword ?? asistente.RequierePassword ?? false;
        if (!requierePassword) {
          const assistantName = asistente.nombre || asistente.Nombre || 'Asistente';
          setChatAssistant(asistente);
          setChatPassword('');
          setChatTitle(`Asistente ${assistantName}`);
          setShowChatModal(true);
        }
      } catch (error) {
        if (isCaptchaError(error)) {
          resetPublicAccess();
          return;
        }
        setDirectError(error.message || 'No pudimos cargar el asistente.');
      } finally {
        setDirectLoading(false);
      }
    };

    loadDirectAssistant();
  }, [assistantNickname, regionalNombreUrl, regionalId, areaId, isPublicAccessVerified]);

  const handleDirectPasswordConfirm = async () => {
    if (!directAssistant) return;
    if (isRagFlowAssistant(directAssistant) && !isAssistantProcessingReady(directAssistant)) {
      setDirectPasswordError(getUnavailableAveMessage(directAssistant));
      return;
    }

    const assistantId = directAssistant.idOpenAIAssistant || directAssistant.IdOpenAIAssistant || directAssistant.id;
    if (!assistantId) {
      setDirectPasswordError('No se encontró el asistente seleccionado.');
      return;
    }
    if (!directPassword.trim()) {
      setDirectPasswordError('Ingresa el password para continuar.');
      return;
    }
    const regionalIdToUse = directRegionalId || regionalId;
    if (!regionalIdToUse) {
      setDirectPasswordError('Falta el regional para validar el password.');
      return;
    }
    if (!areaId) {
      setDirectPasswordError('Falta el área para validar el password.');
      return;
    }

    setDirectValidating(true);
    setDirectPasswordError('');
    try {
      const result = await materiaService.validateAsistentePassword(assistantId, regionalIdToUse, areaId, directPassword.trim());
      const valido = result?.valido ?? result?.Valido;
      if (!valido) {
        setDirectPasswordError('El password ingresado no es correcto.');
        return;
      }
      const assistantName = directAssistant.nombre || directAssistant.Nombre || 'Asistente';
      setChatAssistant(directAssistant);
      setChatPassword(directPassword.trim());
      setChatTitle(`Asistente ${assistantName}`);
      setShowChatModal(true);
    } catch (error) {
      if (isCaptchaError(error)) {
        resetPublicAccess();
        return;
      }
      setDirectPasswordError(error.message || 'No pudimos validar el password.');
    } finally {
      setDirectValidating(false);
    }
  };
  useEffect(() => {
    const fetchMaterias = async () => {
      if (!isPublicAccessVerified) {
        setLoadingMaterias(false);
        return;
      }

      setLoadingMaterias(true);
      setMateriasError('');
      try {
        if (!regionalId || !areaId) {
          setMateriasError(areaError || regionalError || 'Usa el enlace público con regional y área para ver las materias disponibles.');
          setPublicYears([]);
          return;
        }
        const list = await materiaService.getMateriasPublicasPorAnioConAsistentesGpt(regionalId, areaId);
        setPublicYears(Array.isArray(list) ? list : []);
      } catch (error) {
        if (isCaptchaError(error)) {
          resetPublicAccess();
          return;
        }
        setMateriasError(error.message || 'No pudimos cargar las materias públicas.');
      } finally {
        setLoadingMaterias(false);
      }
    };
    fetchMaterias();
  }, [regionalId, areaId, regionalError, areaError, isPublicAccessVerified]);

  const handleAssistantClick = (materia, assistant) => {
    if (!materia || !assistant) return;
    if (isRagFlowAssistant(assistant) && !isAssistantProcessingReady(assistant)) {
      setMateriasError(getUnavailableAveMessage(assistant));
      return;
    }

    if (!regionalId || !areaId) {
      setMateriasError(areaError || regionalError || '');
      return;
    }

    setSelectedSubject(materia.nombre || materia.Nombre || 'Materia');
    setSelectedAssistant(assistant);
    setPasswordInput('');
    setPasswordError('');
    setShowModal(true);
  };

  const toggleSubject = (subjectKey) => {
    setExpandedSubjects((current) => ({
      ...current,
      [subjectKey]: !current[subjectKey]
    }));
  };

  const handleConfirm = async () => {
    if (!selectedAssistant) {
      setShowModal(false);
      return;
    }
    if (isRagFlowAssistant(selectedAssistant) && !isAssistantProcessingReady(selectedAssistant)) {
      setPasswordError(getUnavailableAveMessage(selectedAssistant));
      setShowModal(true);
      return;
    }

    const requierePassword = selectedAssistant.requierePassword ?? selectedAssistant.RequierePassword ?? false;
    if (requierePassword) {
      if (!passwordInput.trim()) {
        setPasswordError('Ingresa el password para continuar.');
        setShowModal(true);
        return;
      }
      setIsValidatingPassword(true);
      setPasswordError('');
      try {
        const assistantId = selectedAssistant.idOpenAIAssistant || selectedAssistant.IdOpenAIAssistant || selectedAssistant.id;
        if (!assistantId) {
          setPasswordError('No se encontró el asistente seleccionado.');
          setShowModal(true);
          return;
        }
        const result = await materiaService.validateAsistentePassword(assistantId, regionalId, areaId, passwordInput.trim());
        const valido = result?.valido ?? result?.Valido;
        if (!valido) {
          setPasswordError('El password ingresado no es correcto.');
          setShowModal(true);
          return;
        }
      } catch (error) {
        if (isCaptchaError(error)) {
          resetPublicAccess();
          return;
        }
        setPasswordError(error.message || 'No pudimos validar el password.');
        setShowModal(true);
        return;
      } finally {
        setIsValidatingPassword(false);
      }
    }
    const subjectTitle = selectedSubject;
    const gptUrl = selectedAssistant.gptClasicoURLPublica || selectedAssistant.GPTClasicoURLPublica;
    const requiresPassword = selectedAssistant.requierePassword ?? selectedAssistant.RequierePassword ?? false;
    const chatPasswordValue = requiresPassword ? passwordInput.trim() : '';

    if (gptUrl) {
      setShowModal(false);
      setSelectedSubject('');
      setSelectedAssistant(null);
      setPasswordInput('');
      setPasswordError('');
      window.open(gptUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setChatAssistant(selectedAssistant);
    setChatPassword(chatPasswordValue);
    setChatTitle(subjectTitle ? `Asistente ${subjectTitle}` : 'Asistente');
    setShowModal(false);
    setSelectedSubject('');
    setSelectedAssistant(null);
    setPasswordInput('');
    setPasswordError('');
    setShowChatModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedSubject('');
    setSelectedAssistant(null);
    setPasswordInput('');
    setPasswordError('');
  };

  const handleCloseChat = () => {
    setShowChatModal(false);
    setChatAssistant(null);
    setChatPassword('');
    setChatTitle('');
  };

  const colorPalette = [
    { accent: '#2563eb', soft: 'rgba(37, 99, 235, 0.12)', strong: '#1d4ed8' },
    { accent: '#0f766e', soft: 'rgba(15, 118, 110, 0.12)', strong: '#115e59' },
    { accent: '#ea580c', soft: 'rgba(234, 88, 12, 0.12)', strong: '#c2410c' },
    { accent: '#7c3aed', soft: 'rgba(124, 58, 237, 0.12)', strong: '#6d28d9' },
    { accent: '#be123c', soft: 'rgba(190, 18, 60, 0.12)', strong: '#9f1239' },
    { accent: '#0891b2', soft: 'rgba(8, 145, 178, 0.12)', strong: '#0e7490' },
  ];
  const yearCards = useMemo(() => {
    if (!Array.isArray(publicYears)) return [];
    return publicYears
      .map((year, index) => {
        const valor = year.valor ?? year.Valor;
        const name = valor ? `${valor}º Año` : 'Electivas';
        const palette = colorPalette[index % colorPalette.length];
        const subjects = (year.materias || year.Materias || []).filter((subject) => {
          const assistants = subject?.asistentes || subject?.Asistentes || [];
          return Array.isArray(assistants) && assistants.length > 0;
        });
        const assistantsCount = subjects.reduce((total, subject) => {
          const assistants = subject?.asistentes || subject?.Asistentes || [];
          return total + (Array.isArray(assistants) ? assistants.length : 0);
        }, 0);

        return {
          key: year.idAnio || year.IdAnio || name,
          name,
          palette,
          subjects,
          assistantsCount
        };
      })
      .filter((year) => year.subjects.length > 0);
  }, [publicYears]);

  const catalogSummary = useMemo(() => {
    return yearCards.reduce(
      (acc, year) => ({
        subjects: acc.subjects + year.subjects.length,
        assistants: acc.assistants + year.assistantsCount
      }),
      { subjects: 0, assistants: 0 }
    );
  }, [yearCards]);

  useEffect(() => {
    setExpandedSubjects({});
  }, [publicYears]);

  useEffect(() => {
    if (!Array.isArray(publicYears)) {
      setAreaName('');
      setRegionalName('');
      return;
    }
    const firstMateria = publicYears
      .flatMap((year) => year.materias || year.Materias || [])
      .find((materia) => materia?.nombreArea || materia?.NombreArea);
    const name = firstMateria?.nombreArea || firstMateria?.NombreArea || '';
    setAreaName(name);
    const regional = firstMateria?.nombreRegional || firstMateria?.NombreRegional || '';
    setRegionalName(regional);
  }, [publicYears]);

  const directMode = Boolean(assistantNickname);
  if (directMode) {
    const requiresPassword = directAssistant?.requierePassword ?? directAssistant?.RequierePassword ?? false;
    const assistantName = directAssistant?.nombre || directAssistant?.Nombre || assistantNickname || 'Asistente';
    const showDirectModal = directLoading || directError;
    const directTitle = directLoading ? 'Preparando asistente' : 'No pudimos abrir el asistente';
    const directMessage = directLoading
      ? 'Estamos cargando la informacion necesaria para iniciar el chat.'
      : directError;

    return (
      <div className="subject-cards-container">
        {!isPublicAccessVerified && (
          <PublicCaptchaGate onVerified={() => setIsPublicAccessVerified(true)} />
        )}
        {showDirectModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                padding: '2rem',
                maxWidth: '420px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>
                {directLoading ? '⏳' : '⚠️'}
              </div>
              <h3 style={{ marginTop: 0 }}>{directTitle}</h3>
              <p style={{ color: '#475569', marginBottom: '1.25rem' }}>{directMessage}</p>
              {directLoading ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem' }}>
                  <i className="fas fa-spinner fa-spin" />
                  <span style={{ fontWeight: 600 }}>Cargando...</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => window.location.assign('/')}
                  style={{
                    marginTop: '0.25rem',
                    padding: '0.7rem 1.4rem',
                    border: 'none',
                    borderRadius: '999px',
                    backgroundColor: '#0f172a',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Volver al inicio
                </button>
              )}
            </div>
          </div>
        )}

        {!directLoading && !directError && requiresPassword && !showChatModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9998
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '18px',
                padding: '2rem',
                maxWidth: '420px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)',
                textAlign: 'left'
              }}
            >
              <p style={{ marginTop: 0, marginBottom: '0.35rem', color: '#64748b', fontWeight: 600, letterSpacing: '0.04em' }}>
                Acceso privado
              </p>
              <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Asistente {assistantName}</h3>
              <p style={{ color: '#475569', marginBottom: '1.25rem' }}>
                Este asistente requiere password para iniciar el chat.
              </p>
              <div style={{ maxWidth: '320px' }}>
                <input
                  type="password"
                  value={directPassword}
                  onChange={(e) => setDirectPassword(e.target.value)}
                  placeholder="Password del asistente"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}
                />
                {directPasswordError && (
                  <p style={{ marginTop: '0.5rem', color: '#b91c1c', fontWeight: 600 }}>
                    {directPasswordError}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleDirectPasswordConfirm}
                  disabled={directValidating}
                  style={{
                    marginTop: '0.9rem',
                    width: '100%',
                    padding: '0.75rem',
                    border: 'none',
                    backgroundColor: '#0f172a',
                    color: 'white',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: 700
                  }}
                >
                  {directValidating ? 'Validando...' : 'Iniciar chat'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showChatModal && (
          <ChatModal
            isOpen={showChatModal}
            onClose={handleCloseChat}
            assistant={chatAssistant}
            publicChat
            regionalId={directRegionalId || regionalId}
            areaId={areaId}
            password={chatPassword || null}
            headerTitle={chatTitle || `Asistente ${assistantName}`}
          />
        )}
      </div>
    );
  }

  const showPageLoading = isResolvingRegional || loadingMaterias;

  return (
    <div className="subject-cards-container">
      {!isPublicAccessVerified && (
        <PublicCaptchaGate onVerified={() => setIsPublicAccessVerified(true)} />
      )}
      {showPageLoading && (
        <div className="page-loading-overlay">
          <div className="page-loading-card">
            <i className="fas fa-spinner fa-spin" />
            <span>Cargando información del área...</span>
          </div>
        </div>
      )}
      {/* Header con logo */}
      <div className="header-section">
        <div className="container">
          <div className="row justify-content-center align-items-center">
            <div className="col-12 text-center">
              <div className="logo-container mb-4">
                <img 
                  src={utnFrcLogo} 
                  alt="UTN FRC Logo" 
                  className="utn-logo"
                />
                <img 
                  src={logoAve} 
                  alt="Logo Ave" 
                  className="ave-logo"
                />
              </div>
              <h1 className="main-title">
                <span className="title-gradient">{areaName || 'Catálogo de asistentes GPT clásicos'}</span>
              </h1>
              <h2 className="subtitle">
                {regionalName
                  ? `Universidad Tecnológica Nacional - ${regionalName}`
                  : 'Universidad Tecnológica Nacional'}
              </h2>
              <div className="decorative-line mx-auto"></div>
              {!loadingMaterias && !isResolvingRegional && !materiasError && yearCards.length > 0 && (
                <div className="catalog-summary">
                  <div className="catalog-summary__location">
                    <span className="catalog-summary__label">Área actual</span>
                    <strong>{areaName || 'Catálogo de asistentes GPT clásicos'}</strong>
                    {regionalName && (
                      <small>{regionalName}</small>
                    )}
                  </div>
                  <p className="catalog-summary__copy">
                    Explorá las materias con asistentes disponibles en esta área. El trayecto se organiza en 5 años de cursado más las electivas, y al seleccionar un asistente primero verás el consentimiento antes de continuar.
                  </p>
                  <div className="catalog-summary__chips">
                    <span className="catalog-summary__chip catalog-summary__chip--accent">
                      <strong>5 años + electivas</strong>
                      Trayecto académico
                    </span>
                    <span className="catalog-summary__chip">
                      <strong>{catalogSummary.subjects}</strong>
                      {catalogSummary.subjects === 1 ? ' materia con asistentes' : ' materias con asistentes'}
                    </span>
                    <span className="catalog-summary__chip catalog-summary__chip--highlight">
                      <strong>{catalogSummary.assistants}</strong>
                      {catalogSummary.assistants === 1 ? ' agente GPT disponible' : ' agentes GPT disponibles'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grid de cards */}
      <div className="container my-5">
        <div className="row g-4">
          {(loadingMaterias || isResolvingRegional) && (
            <div className="col-12">
              <div className="alert alert-info text-center">
                <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }} />
                Estamos obteniendo el listado de materias y asistentes...
              </div>
            </div>
          )}
          {!loadingMaterias && !isResolvingRegional && materiasError && (
            <div className="col-12">
              <div className="alert alert-danger text-center">{materiasError}</div>
            </div>
          )}
          {!loadingMaterias && !isResolvingRegional && !materiasError && yearCards.length === 0 && (
            <div className="col-12">
              <div className="alert alert-warning text-center">No hay materias públicas disponibles.</div>
            </div>
          )}
          {!loadingMaterias && !isResolvingRegional && !materiasError && yearCards.map((yearData) => (
            <div key={yearData.key} className="col-12 col-md-6 col-lg-4">
              <div
                className="subject-card year-panel h-100"
                style={{
                  '--year-accent': yearData.palette.accent,
                  '--year-accent-soft': yearData.palette.soft,
                  '--year-accent-strong': yearData.palette.strong
                }}
              >
                <div className="card-header year-panel__header">
                  <div className="year-panel__title-wrap">
                    <span className="year-panel__eyebrow">Trayecto</span>
                    <h5 className="card-title mb-0 d-flex align-items-center">
                      <span className="me-2 fs-4">🎓</span>
                      {yearData.name}
                    </h5>
                  </div>
                  <div className="year-panel__metrics">
                    <div className="year-panel__count">
                      <span>{yearData.subjects.length}</span>
                      <small>{yearData.subjects.length === 1 ? 'materia' : 'materias'}</small>
                    </div>
                    <div className="year-panel__count year-panel__count--secondary">
                      <span>{yearData.assistantsCount}</span>
                      <small>{yearData.assistantsCount === 1 ? 'GPT' : 'GPTs'}</small>
                    </div>
                  </div>
                </div>
                <div className="card-body year-panel__body">
                  <div className="links-container year-panel__subjects">
                    {yearData.subjects.map((subject, index) => {
                      const assistants = subject.asistentes || subject.Asistentes || [];
                      const assistantsCount = Array.isArray(assistants) ? assistants.length : 0;
                      const tipoMateria = subject.tipoMateria || subject.TipoMateria || '';
                      const subjectKey = `${yearData.key}-${subject.id || subject.Id || index}`;
                      const isExpanded = Boolean(expandedSubjects[subjectKey]);
                      return (
                        <article
                          key={subject.id || subject.Id || index}
                          className={`subject-link-item subject-entry${isExpanded ? ' subject-entry--expanded' : ''}`}
                        >
                          <button
                            type="button"
                            className="subject-entry__toggle"
                            onClick={() => toggleSubject(subjectKey)}
                            aria-expanded={isExpanded}
                          >
                            <div className="subject-item-header">
                              <div className="subject-item-title">
                                <i className="fas fa-book-open" />
                                <span>{subject.nombre || subject.Nombre || 'Materia'}</span>
                              </div>
                              <div className="subject-entry__badges">
                                {tipoMateria && (
                                  <span className="subject-entry__badge">{tipoMateria}</span>
                                )}
                                <span className="subject-entry__badge subject-entry__badge--accent">
                                  {assistantsCount} {assistantsCount === 1 ? 'GPT' : 'GPTs'}
                                </span>
                              </div>
                            </div>
                            <div className="subject-entry__toggle-footer">
                              <small>
                                {isExpanded
                                  ? 'Ocultar asistentes disponibles'
                                  : 'Ver asistentes disponibles'}
                              </small>
                              <span className={`subject-entry__chevron${isExpanded ? ' subject-entry__chevron--expanded' : ''}`}>
                                <i className="fas fa-chevron-down"></i>
                              </span>
                            </div>
                          </button>
                          {isExpanded && (
                            <div className="subject-entry__content">
                              {(subject.descripcion || subject.Descripcion) && (
                                <p className="subject-entry__description">
                                  {subject.descripcion || subject.Descripcion}
                                </p>
                              )}
                              <div className="subject-entry__assistants-title">
                                <span>Asistentes disponibles</span>
                                <small>Al seleccionar uno, se pedirá tu consentimiento antes de continuar.</small>
                              </div>
                              <div className="assistant-links">
                                {assistants.map((assistant, assistantIndex) => {
                                  const requiresPassword = assistant.requierePassword ?? assistant.RequierePassword;
                                  const assistantDescription = assistant.descripcion || assistant.Descripcion || '';
                                  const isUnavailableAve = isRagFlowAssistant(assistant) && !isAssistantProcessingReady(assistant);
                                  const processingLabel = getAssistantProcessingLabel(assistant);
                                  const processingPercent = getAssistantProcessingPercent(assistant);
                                  const actionLabel = isUnavailableAve
                                    ? `${processingLabel}${processingPercent !== null ? ` ${processingPercent}%` : ''}`
                                    : (requiresPassword ? 'Con clave' : 'Abrir');
                                  return (
                                    <button
                                      key={assistant.idOpenAIAssistant || assistant.IdOpenAIAssistant || assistant.id || assistantIndex}
                                      onClick={() => handleAssistantClick(subject, assistant)}
                                      className={`btn btn-sm w-100 text-start assistant-link-button${isUnavailableAve ? ' assistant-link-button--disabled' : ''}`}
                                      type="button"
                                      disabled={isUnavailableAve}
                                      title={isUnavailableAve ? getUnavailableAveMessage(assistant) : undefined}
                                    >
                                      <span className="assistant-link-main">
                                        <span className="assistant-link-icon">
                                          <i className="fas fa-robot"></i>
                                        </span>
                                        <span className="assistant-link-copy">
                                          <strong>{assistant.nombre || assistant.Nombre || 'Asistente GPT'}</strong>
                                          <small>
                                            {isUnavailableAve
                                              ? (getAssistantProcessingDetail(assistant) || 'Procesando documentos en RagFlow')
                                              : (assistantDescription || (requiresPassword ? 'Requiere validación antes de ingresar' : 'Acceso directo al GPT clásico'))}
                                          </small>
                                        </span>
                                      </span>
                                      <span className="assistant-link-side">
                                        <span className={`assistant-link-action${requiresPassword ? ' assistant-link-action--locked' : ''}${isUnavailableAve ? ' assistant-link-action--processing' : ''}`}>
                                          {actionLabel}
                                        </span>
                                        {requiresPassword && (
                                          <span className="assistant-lock" title="Requiere password">
                                            <i className="fas fa-lock"></i>
                                          </span>
                                        )}
                                        {!isUnavailableAve && (
                                          <span className="assistant-link-arrow">
                                            <i className="fas fa-arrow-up-right-from-square"></i>
                                          </span>
                                        )}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="footer-section mt-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <p className="footer-text">
                © {currentYear} UTN FRC - {areaName || 'Área académica'}
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal de Advertencia */}
      {showModal && (
        <div
          className="consent-modal-backdrop"
          onClick={handleCancel}
        >
          <div
            className="consent-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="consent-modal__header">
              <div className="consent-modal__icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <p className="consent-modal__eyebrow">Consentimiento previo</p>
              <h2 className="consent-modal__title">Antes de ingresar al asistente</h2>
            </div>

            <div className="consent-modal__body">
              {selectedAssistant && (
                <div className="consent-modal__content">
                  <p className="consent-modal__lead">
                    A continuación accederás al asistente de la materia:
                  </p>
                  <p className="consent-modal__subject">
                    <i className="fas fa-book-open"></i>
                    <span>{selectedSubject}</span>
                  </p>
                  {selectedAssistant && (
                    <p className="consent-modal__assistant">
                      <span>Asistente</span>
                      <strong>{selectedAssistant.nombre || selectedAssistant.Nombre}</strong>
                    </p>
                  )}
                  <div className="consent-modal__notice">
                    <p>
                      Las respuestas generadas por este asistente tienen carácter orientativo y deben considerarse como apoyo al proceso de aprendizaje.
                    </p>
                    <p>
                      Se recomienda contrastar la información con las fuentes oficiales de la cátedra o con el equipo docente.
                    </p>
                  </div>
                  <p className="consent-modal__hint">
                    Si aceptás, el GPT clásico se abrirá en una nueva pestaña.
                  </p>
                  {(selectedAssistant.requierePassword ?? selectedAssistant.RequierePassword) && (
                    <div className="consent-modal__field">
                      <label className="consent-modal__label">
                        Password de acceso
                      </label>
                      <input
                        type="password"
                        value={passwordInput}
                        onChange={(e) => setPasswordInput(e.target.value)}
                        placeholder="Ingresa el password del asistente"
                        className="consent-modal__input"
                      />
                      {passwordError && (
                        <p className="consent-modal__error">{passwordError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="consent-modal__actions">
              <button
                onClick={handleCancel}
                className="consent-modal__button consent-modal__button--secondary"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={isValidatingPassword || !selectedAssistant}
                className="consent-modal__button consent-modal__button--primary"
              >
                {isValidatingPassword ? 'Validando...' : 'Entendido, continuar'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Chat Assistant */}
      {showChatModal && (
        <ChatModal
          isOpen={showChatModal}
          onClose={handleCloseChat}
          assistant={chatAssistant}
          publicChat
          regionalId={regionalId}
          areaId={areaId}
          password={chatPassword || null}
          headerTitle={chatTitle}
        />
      )}
    </div>
  );
};

export default SubjectCardsGrid;
